import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { TaskTemplateDeadlineAnchor } from "@common/types/taskTemplate";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    DEADLINE_ANCHOR_LABEL,
    DEADLINE_ANCHOR_OPTIONS,
    DEFAULT_DEADLINE_ANCHOR,
} from "../closeTemplateTasksDrawer.constants";
import {
    CloseTemplateTaskCallbacks,
    CloseTemplateTaskRow,
} from "../closeTemplateTasksViewConfig";

type Meta = ViewTableMeta<CloseTemplateTaskCallbacks, never>;

// Built on first render, not at module load: the constants module imports
// this cell through `closeTemplateTasksViewConfig`, so its exports are not
// initialized yet when this module evaluates.
const buildDeadlineAnchorItems = (): Array<
    PickerItemData<TaskTemplateDeadlineAnchor>
> =>
    DEADLINE_ANCHOR_OPTIONS.map((anchor) => ({
        value: anchor,
        label: DEADLINE_ANCHOR_LABEL[anchor],
    }));

export const CloseTemplateTaskAnchorDateCell = memo(
    function CloseTemplateTaskAnchorDateCell({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        CloseTemplateTaskRow,
        TaskTemplateDeadlineAnchor | null
    >) {
        const meta = (table.options.meta ?? {}) as Meta;
        const item = row.original;
        const deadlineAnchorItems = useMemo(
            () => buildDeadlineAnchorItems(),
            [],
        );

        if (item.isGroupRow) {
            return null;
        }

        const effectiveAnchor = item.deadlineAnchor ?? DEFAULT_DEADLINE_ANCHOR;

        return (
            <ViewTableCell>
                <Picker.Select
                    trigger="cell"
                    items={deadlineAnchorItems}
                    value={effectiveAnchor}
                    onValueChange={(v) => {
                        if (
                            v === null ||
                            (v === DEFAULT_DEADLINE_ANCHOR &&
                                item.deadlineAnchor == null)
                        ) {
                            return;
                        }
                        meta.callbacks?.onUpdateDeadlineAnchor(item.id, v);
                    }}
                />
            </ViewTableCell>
        );
    },
);
